import { useState, useEffect, useCallback } from 'react';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'PROJECT_MANAGER' | 'EDITOR' | 'TRANSLATOR' | 'DESIGNER' | 'VIEWER' | 'DESIGN_TEAM' | 'LANGUAGE_TEAM';
  languageTeamId?: string;
  designTeamId?: string;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
}

let globalUser: AuthUser | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(() => ({
    user: globalUser,
    loading: true,
  }));

  useEffect(() => {
    // 이미 다른 인스턴스에서 로드 완료된 경우 재호출 불필요
    if (globalUser !== null) {
      setState({ user: globalUser, loading: false });
      const handler = () => setState({ user: globalUser, loading: false });
      listeners.add(handler);
      return () => { listeners.delete(handler); };
    }

    // GET /api/auth/me 로 쿠키 기반 세션 확인
    fetch('/api/auth/me', { credentials: 'include' })
      .then((res) => {
        if (res.ok) return res.json() as Promise<AuthUser>;
        return null;
      })
      .then((user) => {
        globalUser = user;
        setState({ user: globalUser, loading: false });
        notifyListeners();
      })
      .catch(() => {
        globalUser = null;
        setState({ user: null, loading: false });
      });

    const handler = () => setState({ user: globalUser, loading: false });
    listeners.add(handler);
    return () => { listeners.delete(handler); };
  }, []);

  const logout = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).finally(() => {
      globalUser = null;
      notifyListeners();
      window.location.href = '/login';
    });
  }, []);

  const hasRole = useCallback(
    (role: AuthUser['role']) => state.user?.role === role || state.user?.role === 'ADMIN' || state.user?.role === 'SUPER_ADMIN',
    [state.user]
  );

  return {
    user: state.user,
    loading: state.loading,
    logout,
    hasRole,
    isAuthenticated: !!state.user,
  };
}
